import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import type { World } from '../../shared/types.js';
import { Projection } from '../../shared/projection.js';
import type { MinimapPlayer } from '../game/minimap.js';
import { t } from '../i18n/i18n.js';
import { el, hex, icon } from './dom.js';

/**
 * The city on a real map, over the race: OpenStreetMap's own tiles, with every street named,
 * the cars on it and the route through it.
 *
 * The minimap is drawn from the world's geometry, which has no lettering and no more of the
 * city than the region; it says which way the road bends and not what it is called. Asked
 * for directly — a bigger map with the street names on it, because the layout alone does not
 * always say where you are. The tiles are the ones the region picker already draws, so this
 * adds no source and no key.
 *
 * It does not stop anything. The race is multiplayer and carries on; the car coasts while
 * the map has the keyboard, as it does under the Escape menu.
 */

export interface MapSnapshot {
  world: World | null;
  players: MinimapPlayer[];
  /** Index of the gate the local car is heading for; past the last is the finish. */
  nextCheckpoint: number;
  freeDrive: boolean;
}

export interface BigMapOptions {
  /** Fires on open and on close, so the caller can block the controls and poll. */
  onOpenChange(open: boolean): void;
}

/** Street level with the names legible, and a block or two either side of the car. */
const OPEN_ZOOM = 17;

export class BigMap {
  readonly root: HTMLElement;

  private readonly mapNode: HTMLElement;
  private readonly followButton: HTMLButtonElement;
  private map: L.Map | null = null;
  private routeLayer: L.LayerGroup | null = null;
  private carLayer: L.LayerGroup | null = null;
  private routeOf: World | null = null;
  private projection: Projection | null = null;
  private openNow = false;
  /** Whether the view keeps the car in the middle. A drag on the map turns it off. */
  private following = true;
  private lastCheckpoint = -1;

  constructor(private readonly options: BigMapOptions) {
    this.mapNode = el('div', { class: 'big-map-canvas', id: 'big-map-canvas' });
    this.followButton = el(
      'button',
      { class: 'big-map-follow icon-button', type: 'button', id: 'big-map-follow', title: t('Follow your car') },
      icon('my_location'),
    );
    this.followButton.addEventListener('click', () => this.setFollowing(true));
    const close = el(
      'button',
      { class: 'big-map-close icon-button', type: 'button', id: 'big-map-close', title: t('Close'), ariaLabel: t('Close') },
      icon('close'),
    );
    close.addEventListener('click', () => this.close());
    this.root = el(
      'div',
      { class: 'big-map is-hidden', id: 'big-map' },
      el('div', { class: 'big-map-panel' }, this.mapNode, close, this.followButton),
    );
    // A click on the dimmed road around the panel closes it, as a tap outside a sheet does.
    this.root.addEventListener('click', (e) => {
      if (e.target === this.root) this.close();
    });
  }

  get isOpen(): boolean {
    return this.openNow;
  }

  toggle(snapshot: MapSnapshot): void {
    if (this.openNow) this.close();
    else this.open(snapshot);
  }

  open(snapshot: MapSnapshot): void {
    if (this.openNow || !snapshot.world) return;
    this.openNow = true;
    this.root.classList.remove('is-hidden');
    this.ensureMap();
    // Leaflet measured the panel while it was hidden, at nothing by nothing.
    this.map!.invalidateSize();
    this.following = true;
    this.followButton.classList.add('is-hidden');
    this.update(snapshot, true);
    this.options.onOpenChange(true);
  }

  close(): void {
    if (!this.openNow) return;
    this.openNow = false;
    this.root.classList.add('is-hidden');
    this.options.onOpenChange(false);
  }

  /** Moves the cars, and redraws the route when the world or the next gate changed. */
  update(snapshot: MapSnapshot, jump = false): void {
    const world = snapshot.world;
    if (!this.openNow || !this.map || !world) return;
    if (world !== this.routeOf || snapshot.nextCheckpoint !== this.lastCheckpoint) {
      this.drawRoute(world, snapshot);
    }
    const projection = this.projection!;
    const cars = this.carLayer!;
    cars.clearLayers();
    let self: L.LatLng | null = null;
    // The others first, so the player's own arrow is drawn over anybody parked on it.
    for (const player of [...snapshot.players].sort((a, b) => Number(a.self) - Number(b.self))) {
      const at = projection.unproject(player.x, player.z);
      const latLng = L.latLng(at.lat, at.lon);
      if (player.self) self = latLng;
      cars.addLayer(L.marker(latLng, { icon: carIcon(player), interactive: false, keyboard: false }));
    }
    if (self && this.following) {
      if (jump) this.map.setView(self, OPEN_ZOOM, { animate: false });
      else this.map.panTo(self, { animate: true, duration: 0.25 });
    }
  }

  private ensureMap(): void {
    if (this.map) return;
    const map = L.map(this.mapNode, { zoomControl: false, attributionControl: true });
    L.control.zoom({ position: 'bottomright' }).addTo(map);
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '© OpenStreetMap contributors',
      crossOrigin: true,
    }).addTo(map);
    this.routeLayer = L.layerGroup().addTo(map);
    this.carLayer = L.layerGroup().addTo(map);
    // Somebody who drags the map is looking at something; following the car would take it
    // away from them on the next update.
    map.on('dragstart', () => this.setFollowing(false));
    this.map = map;
  }

  private setFollowing(on: boolean): void {
    this.following = on;
    this.followButton.classList.toggle('is-hidden', on);
  }

  private drawRoute(world: World, snapshot: MapSnapshot): void {
    if (world !== this.routeOf) this.projection = new Projection(world.origin);
    this.routeOf = world;
    this.lastCheckpoint = snapshot.nextCheckpoint;
    const projection = this.projection!;
    const layer = this.routeLayer!;
    layer.clearLayers();
    const latLng = (x: number, z: number) => {
      const p = projection.unproject(x, z);
      return L.latLng(p.lat, p.lon);
    };
    // The region's edge, so it is plain where the drivable city stops and the map goes on.
    const { x, z } = world.extent;
    layer.addLayer(
      L.rectangle(L.latLngBounds(latLng(-x, -z), latLng(x, z)), {
        color: '#1b1b1f',
        weight: 1.5,
        dashArray: '6 6',
        fill: false,
        interactive: false,
      }),
    );
    if (snapshot.freeDrive) return;
    const route = world.route;
    const path: L.LatLng[] = [];
    for (let i = 0; i + 1 < route.path.length; i += 2) path.push(latLng(route.path[i], route.path[i + 1]));
    layer.addLayer(L.polyline(path, { color: '#1a73e8', weight: 6, opacity: 0.75, interactive: false }));
    route.checkpoints.forEach((gate, i) => {
      const next = i === snapshot.nextCheckpoint;
      layer.addLayer(
        L.circleMarker(latLng(gate.x, gate.z), {
          radius: next ? 9 : 6,
          color: '#ffffff',
          weight: 2,
          fillColor: i < snapshot.nextCheckpoint ? '#9e9e9e' : next ? '#fbbc04' : '#1a73e8',
          fillOpacity: 1,
          interactive: false,
        }),
      );
    });
    layer.addLayer(
      L.marker(latLng(route.finish.x, route.finish.z), {
        icon: L.divIcon({ className: 'big-map-finish', html: '🏁', iconSize: [28, 28], iconAnchor: [6, 26] }),
        interactive: false,
        keyboard: false,
      }),
    );
  }
}

/**
 * A car as an arrow in its own colour, pointing the way it is driving, with the driver's
 * name beside the others. Built as elements rather than as an HTML string, so a name is
 * never markup — the rule `dom.ts` exists for.
 */
function carIcon(player: MinimapPlayer): L.DivIcon {
  const arrow = el('div', { class: 'big-map-arrow' });
  arrow.style.setProperty('--car', hex(player.color));
  // Game headings turn the other way from compass bearings: forward is (-sin h, -cos h) in a
  // world whose north is -z, so the bearing is -h.
  arrow.style.transform = `rotate(${-player.heading}rad)`;
  const node = el('div', { class: player.self ? 'big-map-car is-self' : 'big-map-car' }, arrow);
  if (!player.self && player.name) node.append(el('span', { class: 'big-map-name' }, player.name));
  return L.divIcon({ className: 'big-map-car-icon', html: node, iconSize: [24, 24], iconAnchor: [12, 12] });
}
